import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { IconAlertTriangle, IconDollar } from "../components/icons";
import type { TransportFinancialDto, CalculationMemoryDto } from "@bi/types";

const AGING_COLORS = ["var(--brand)", "#F59E0B", "#F97316", "#DC2626", "#7C3AED"];

/** Largest-remainder rounding so pie percentages always sum to exactly 100. */
function roundToHundred(values: number[]): number[] {
  const total = values.reduce((s, v) => s + v, 0);
  if (total === 0) return values.map(() => 0);
  const floors = values.map((v) => Math.floor((v / total) * 100));
  const remainders = values.map((v, i) => (v / total) * 100 - floors[i]!);
  const deficit = 100 - floors.reduce((s, v) => s + v, 0);
  const order = remainders
    .map((r, i) => ({ r, i }))
    .sort((a, b) => b.r - a.r)
    .map((x) => x.i);
  const result = [...floors];
  for (let k = 0; k < deficit; k++) result[order[k]!]!++;
  return result;
}

// Cards com memória de cálculo ativa. Demais exibem tooltip no hover.
const MEMORY_CARDS: Record<string, string> = {
  "a-receber-em-aberto": "a-receber-em-aberto",
  "a-pagar-em-aberto": "a-pagar-em-aberto",
  "recebido-no-periodo": "recebido-no-periodo",
  "pago-no-periodo": "pago-no-periodo",
  "inadimplencia-total": "inadimplencia-total",
  "pagamentos-atrasados": "pagamentos-atrasados",
  "saldo-bancario": "saldo-bancario",
  "fluxo-projetado-30d": "fluxo-projetado-30d",
};

/**
 * Cards cujo valor É a soma de um intervalo (date_range na memória de cálculo).
 * Só nestes o drill herda o período do filtro global — nos demais (posição em
 * aberto, vencido, saldo, projeção 30d) a janela é fixa por definição e mandar
 * `periodFrom/periodTo` mudaria a regra do card.
 */
const PERIOD_SENSITIVE_CARDS = new Set(["recebido-no-periodo", "pago-no-periodo"]);

/** Rótulo dos cards que mostram POSIÇÃO ATUAL e, por isso, não seguem o período. */
const SNAPSHOT_HINT = "posição atual · não segue o período";

function MemoryCardWrapper({
  cardKey,
  children,
  navigate,
  periodQS,
}: {
  cardKey: string;
  children: React.ReactNode;
  navigate: (path: string) => void;
  periodQS: string;
}) {
  const hasMemory = Boolean(MEMORY_CARDS[cardKey]);
  if (hasMemory) {
    const qs = PERIOD_SENSITIVE_CARDS.has(cardKey) ? periodQS : "";
    return (
      <div
        className="cursor-pointer"
        onClick={() => navigate(`/financeiro/memoria/${cardKey}${qs}`)}
        title="Clique para ver a memória de cálculo"
      >
        {children}
      </div>
    );
  }
  return (
    <div className="cursor-default" title="Memória de cálculo em desenvolvimento">
      {children}
    </div>
  );
}

export function FinancialPage() {
  const navigate = useNavigate();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);
  // Repassado ao drill dos cards de intervalo para que a memória de cálculo abra
  // no MESMO período do card (antes o drill caía sempre no mês corrente).
  const periodQS = `?periodFrom=${encodeURIComponent(from)}&periodTo=${encodeURIComponent(to)}`;

  const { data: fin, isLoading } = useQuery({
    queryKey: ["transport", "financial", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportFinancialDto>(
        `/dashboards/transport/financial?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  // Rankings de 30 dias direto da memória de cálculo — MESMA regra do drill (sem duplicar cálculo).
  const { data: due30 } = useQuery({
    queryKey: ["calculation-memory-mini", "recebimentos-previstos-30d"],
    queryFn: () =>
      api.get<CalculationMemoryDto>("/calculation-memory/recebimentos-previstos-30d?limit=8&sortBy=value_desc").catch(() => null),
    staleTime: 60_000,
  });
  const { data: overdueRecent } = useQuery({
    queryKey: ["calculation-memory-mini", "inadimplencia-recente-30d"],
    queryFn: () =>
      api.get<CalculationMemoryDto>("/calculation-memory/inadimplencia-recente-30d?limit=8&sortBy=value_desc").catch(() => null),
    staleTime: 60_000,
  });

  // ── Deltas via computeDelta ────────────────────────────────────────────────
  // AR open / AP open: sem data_baixa histórica — delta indisponível por design
  const arOpenDelta  = computeDelta(null, null, { direction: "neutral" });
  const apOpenDelta  = computeDelta(null, null, { direction: "neutral" });

  const receivedDelta = computeDelta(fin?.receivedMtd, fin?.receivedPrevMtd, {
    direction: "higher_is_better",
  });
  const paidDelta = computeDelta(fin?.paidMtd, fin?.paidPrevMtd, {
    direction: "higher_is_worse",
  });
  const arOverdueDelta = computeDelta(fin?.arOverdueTotal, fin?.arOverduePrev1M, {
    direction: "higher_is_worse",
  });
  const apOverdueDelta = computeDelta(fin?.apOverdueTotal, fin?.apOverduePrev1M, {
    direction: "higher_is_worse",
  });

  // Saldo bancário: sem histórico de saldo — delta indisponível por design
  const cashBalDelta = computeDelta(null, null, { direction: "neutral" });

  // Fluxo projetado 30d vs realizado últimos 30d
  const gap30d = fin?.cashGap30d
    ? fin.cashGap30d.receivable - fin.cashGap30d.payable
    : null;
  const gap30dDelta = computeDelta(gap30d, fin?.realizedNet30d, {
    direction: "higher_is_better",
  });

  const aging   = fin?.arAging   ?? [];
  const debtors = fin?.topDebtors ?? [];
  const due30List     = useProgressiveList(due30?.breakdown.rows ?? []);
  const overdueList   = useProgressiveList(overdueRecent?.breakdown.rows ?? []);
  const debtorList    = useProgressiveList(debtors);
  const supplierList  = useProgressiveList(fin?.topSuppliers ?? []);
  const monthlyData = (fin?.monthlyFlow && fin.monthlyFlow.length > 0) ? fin.monthlyFlow : [];

  // Pie chart: apply largest-remainder rounding so labels always sum to 100%
  const agingPcts = roundToHundred(aging.map((b) => b.amount));
  const agingWithPct = aging.map((b, i) => ({ ...b, displayPct: agingPcts[i] ?? 0 }));
  const agingPctByBucket = new Map(agingWithPct.map((b) => [b.bucket, b.displayPct]));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Financeiro"
        subtitle="Controle de recebíveis, pagamentos, inadimplência e saldo"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        <MemoryCardWrapper cardKey="a-receber-em-aberto" navigate={navigate} periodQS={periodQS}>
          <KPIStatCard
            label="A receber em aberto"
            value={fin ? fmtBRL(fin.arOpenTotal) : "—"}
            delta={arOpenDelta}
            comparisonLabel={SNAPSHOT_HINT}
            status="good"
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="a-pagar-em-aberto" navigate={navigate} periodQS={periodQS}>
          <KPIStatCard
            label="A pagar em aberto"
            value={fin ? fmtBRL(fin.apOpenTotal) : "—"}
            delta={apOpenDelta}
            comparisonLabel={SNAPSHOT_HINT}
            status="neutral"
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="recebido-no-periodo" navigate={navigate} periodQS={periodQS}>
          <KPIStatCard
            label="Recebido no período"
            value={fin ? fmtBRL(fin.receivedMtd) : "—"}
            delta={receivedDelta}
            comparisonLabel={fin?.comparisonLabel ?? null}
            status="good"
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="pago-no-periodo" navigate={navigate} periodQS={periodQS}>
          <KPIStatCard
            label="Pago no período"
            value={fin ? fmtBRL(fin.paidMtd) : "—"}
            delta={paidDelta}
            comparisonLabel={fin?.comparisonLabel ?? null}
            status="neutral"
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="inadimplencia-total" navigate={navigate} periodQS={periodQS}>
          <KPIStatCard
            label="Inadimplência total"
            value={fin ? fmtBRL(fin.arOverdueTotal) : "—"}
            delta={arOverdueDelta}
            comparisonLabel="vs. mesmo dia 1 mês atrás"
            status={fin && fin.arOverdueTotal > 0 ? "critical" : "good"}
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="pagamentos-atrasados" navigate={navigate} periodQS={periodQS}>
          <KPIStatCard
            label="Pagamentos atrasados"
            value={fin ? fmtBRL(fin.apOverdueTotal) : "—"}
            delta={apOverdueDelta}
            comparisonLabel="vs. mesmo dia 1 mês atrás"
            status={fin && fin.apOverdueTotal > 0 ? "warning" : "good"}
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="saldo-bancario" navigate={navigate} periodQS={periodQS}>
          <KPIStatCard
            label="Saldo bancário"
            value={fin ? fmtBRL(fin.cashBalance) : "—"}
            delta={cashBalDelta}
            comparisonLabel={SNAPSHOT_HINT}
            status={fin ? (fin.cashBalance >= 0 ? "good" : "critical") : "neutral"}
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="fluxo-projetado-30d" navigate={navigate} periodQS={periodQS}>
          <KPIStatCard
            label="Fluxo projetado 30d"
            value={gap30d != null ? fmtBRL(gap30d) : "—"}
            delta={gap30dDelta}
            comparisonLabel="vs. realizado últimos 30 dias"
            status={gap30d != null ? (gap30d >= 0 ? "good" : "warning") : "neutral"}
            loading={isLoading}
          />
        </MemoryCardWrapper>
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Recebimentos × Pagamentos" subtitle="Últimos 5 meses · barras = fluxo · linha = saldo do mês" height={260}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={monthlyData.map((m) => ({ ...m, net: (m.received ?? 0) - (m.paid ?? 0) }))}
              margin={{ top: 8, right: 8, left: -10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v: number, n: string) => [fmtBRL(v), n === "received" ? "Recebido" : n === "paid" ? "Pago" : "Saldo"]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                formatter={(n: string) => (n === "received" ? "Recebido" : n === "paid" ? "Pago" : "Saldo (recebido − pago)")} />
              <Bar dataKey="received" fill="var(--brand)" radius={[3, 3, 0, 0]} name="received" />
              <Bar dataKey="paid"     fill="#F59E0B" radius={[3, 3, 0, 0]} name="paid" />
              <Line type="monotone" dataKey="net" stroke="#0F766E" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} name="net" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Inadimplência por Faixa" subtitle="Valores em aberto por aging" height={280}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={agingWithPct}
                dataKey="amount"
                nameKey="bucket"
                cx="50%"
                cy="45%"
                outerRadius={80}
              >
                {agingWithPct.map((_, i) => <Cell key={i} fill={AGING_COLORS[i % AGING_COLORS.length]} />)}
              </Pie>
              <Tooltip
                formatter={(v: number, _: string, entry: { payload?: { bucket?: string; displayPct?: number } }) =>
                  [fmtBRL(v), `${entry.payload?.bucket ?? ""} (${entry.payload?.displayPct ?? 0}%)`]
                }
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                formatter={(value: string) => `${value}: ${agingPctByBucket.get(value) ?? 0}%`}
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Contas a pagar: agenda por faixa + compromissos por semana */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Contas a Pagar por Faixa de Vencimento</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Vencimentos até 30 dias · faixas mutuamente exclusivas · clique para ver os títulos
              {fin?.apScheduleSource === "sinersoft"
                ? " · valores do ERP ao vivo (fecham com os cards do topo); o detalhamento vem do snapshot sincronizado"
                : " · snapshot sincronizado ⚠️ (ERP indisponível — pode divergir dos cards do topo)"}
            </p>
          </div>
          <div className="p-5 space-y-3">
            {isLoading ? (
              <p className="text-sm text-gray-400">Carregando…</p>
            ) : (() => {
              // "Acima de 30 dias" removido do card (não é útil no acompanhamento) — só faixas até 30d.
              const sched = (fin?.apSchedule ?? []).filter((b) => b.key !== "d30_plus");
              if (sched.length === 0) return <p className="text-sm text-gray-400">Sem títulos a pagar nos próximos 30 dias.</p>;
              const maxAmt = Math.max(...sched.map((b) => b.amount), 1);
              const farFuture = (from: string) =>
                new Date(new Date(from).getTime() + 3650 * 86400000).toISOString();
              return sched.map((b) => (
                <div
                  key={b.key}
                  className="cursor-pointer group"
                  title="Ver os títulos desta faixa"
                  onClick={() => {
                    if (b.key === "vencidas") { navigate("/financeiro/memoria/pagamentos-atrasados"); return; }
                    if (!b.from) return;
                    const to = b.to ?? farFuture(b.from);
                    navigate(`/financeiro/memoria/pagamentos-previstos-30d?periodFrom=${encodeURIComponent(b.from)}&periodTo=${encodeURIComponent(to)}`);
                  }}
                >
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 group-hover:text-tm-700">{b.label} <span className="text-gray-400">({b.count})</span></span>
                    <span className="font-semibold text-gray-900">{b.formatted}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${b.key === "vencidas" ? "bg-red-500" : b.key === "hoje" ? "bg-amber-500" : "bg-tm-500"}`}
                      style={{ width: `${Math.max(3, Math.round((b.amount / maxAmt) * 100))}%` }}
                    />
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Compromissos das Próximas Semanas</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Contas a pagar por semana de vencimento (próximos 28 dias) · clique para ver os títulos
              {fin?.apScheduleSource === "sinersoft"
                ? " · valores do ERP ao vivo"
                : " · snapshot sincronizado ⚠️ (ERP indisponível)"}
            </p>
          </div>
          <div className="p-5 space-y-3">
            {isLoading ? (
              <p className="text-sm text-gray-400">Carregando…</p>
            ) : (fin?.apByDueWeek ?? []).length === 0 ? (
              <p className="text-sm text-gray-400">Sem vencimentos nos próximos 28 dias.</p>
            ) : (() => {
              const weeks = fin!.apByDueWeek;
              const maxAmt = Math.max(...weeks.map((w) => w.amount), 1);
              const fmtDay = (ymd: string) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}`;
              return weeks.map((w, i) => {
                const wStart = `${w.week}T00:00:00.000Z`;
                const wEnd = new Date(new Date(wStart).getTime() + 6 * 86400000).toISOString().slice(0, 10);
                const wTo = new Date(new Date(wStart).getTime() + 7 * 86400000).toISOString();
                // "Esta semana" (i=0) começa HOJE — o drill não lista o que já venceu nesta semana.
                const wFrom = i === 0 ? `${new Date().toISOString().slice(0, 10)}T00:00:00.000Z` : wStart;
                return (
                  <div
                    key={w.week}
                    className="cursor-pointer group"
                    title="Ver os títulos a pagar desta semana"
                    onClick={() => navigate(`/financeiro/memoria/pagamentos-previstos-30d?periodFrom=${encodeURIComponent(wFrom)}&periodTo=${encodeURIComponent(wTo)}`)}
                  >
                    <div className="flex justify-between items-baseline text-sm mb-1">
                      <span className="text-gray-600 group-hover:text-tm-700">
                        <span className="font-medium">{i === 0 ? "Esta semana" : `${fmtDay(w.week)}–${fmtDay(wEnd)}`}</span>
                        <span className="text-gray-400"> · {w.count} título(s)</span>
                      </span>
                      <span className="font-semibold text-gray-900">{w.formatted}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${i === 0 ? "bg-amber-500" : "bg-tm-500"}`}
                        style={{ width: `${Math.max(3, Math.round((w.amount / maxAmt) * 100))}%` }}
                      />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* Próximos 30 dias: quem vai pagar × quem deveria ter pago */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Top Clientes a Receber (30 dias)</p>
              <p className="text-xs text-gray-400 mt-0.5">Títulos a vencer de hoje a +30 dias · previsão contratual</p>
            </div>
            <button className="text-xs text-tm-700 hover:underline" onClick={() => navigate("/financeiro/memoria/recebimentos-previstos-30d")}>ver tudo →</button>
          </div>
          <div className="divide-y divide-surface-border">
            {(due30?.breakdown.rows ?? []).length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem títulos a vencer nos próximos 30 dias.</p>
            ) : due30List.visible.map((r, i) => (
              <div key={r.groupKey} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/financeiro/memoria/recebimentos-previstos-30d?group=${encodeURIComponent(r.groupKey)}`)}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{r.groupLabel}</p>
                  <p className="text-xs text-gray-400">{r.recordCount} título(s) · {r.percentageOfTotal.toFixed(1)}% do total</p>
                </div>
                <p className="text-sm font-semibold text-tm-700">{fmtBRL(r.total)}</p>
              </div>
            ))}
            <LoadMoreBar list={due30List} label="clientes" />
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Deveriam Ter Pago (últimos 30 dias)</p>
              <p className="text-xs text-gray-400 mt-0.5">Vencidos nos últimos 30 dias e ainda em aberto · inadimplência recente</p>
            </div>
            <button className="text-xs text-tm-700 hover:underline" onClick={() => navigate("/financeiro/memoria/inadimplencia-recente-30d")}>ver tudo →</button>
          </div>
          <div className="divide-y divide-surface-border">
            {(overdueRecent?.breakdown.rows ?? []).length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Nenhum título venceu sem pagamento nos últimos 30 dias.</p>
            ) : overdueList.visible.map((r, i) => (
              <div key={r.groupKey} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/financeiro/memoria/inadimplencia-recente-30d?group=${encodeURIComponent(r.groupKey)}`)}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{r.groupLabel}</p>
                  <p className="text-xs text-gray-400">{r.recordCount} título(s) · {r.percentageOfTotal.toFixed(1)}% do total</p>
                </div>
                <p className="text-sm font-semibold text-danger-600">{fmtBRL(r.total)}</p>
              </div>
            ))}
            <LoadMoreBar list={overdueList} label="clientes" />
          </div>
        </div>
      </div>

      {/* Top debtors + suppliers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top debtors */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconAlertTriangle className="w-4 h-4 text-danger-600" />
              Top Inadimplentes
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {debtors.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">
                {isLoading ? "Carregando…" : "Nenhum devedor identificado — sincronização de clientes pendente."}
              </p>
            ) : debtorList.visible.map((d, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                title="Ver composição da inadimplência por cliente"
                onClick={() => navigate("/financeiro/memoria/inadimplencia-total")}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{d.name}</p>
                  <p className="text-xs text-gray-400">Mais antigo: {d.oldestDueDays} dias</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-danger-600">{d.formatted}</p>
                </div>
              </div>
            ))}
            <LoadMoreBar list={debtorList} label="devedores" />
          </div>
        </div>

        {/* Top suppliers */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconDollar className="w-4 h-4 text-warning-600" />
              Top Fornecedores Pagos
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : (fin?.topSuppliers ?? []).length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados de pagamentos.</p>
            ) : supplierList.visible.map((s, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                title="Ver pagamentos por fornecedor"
                onClick={() => navigate("/financeiro/memoria/pago-no-periodo")}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <p className="flex-1 text-sm text-gray-800 truncate">{s.name}</p>
                <p className="text-sm font-semibold text-gray-900">{s.formatted}</p>
              </div>
            ))}
            <LoadMoreBar list={supplierList} label="fornecedores" />
          </div>
        </div>
      </div>
    </div>
  );
}
